import React, { ComponentType, useEffect, useRef } from 'react';
import { useModal } from 'glints-aries/es/@next';
import { omit } from 'lodash-es';
import { useHistory, useLocation } from 'react-router';

import {
  AutoModalPriority,
  useAutoModal,
} from '../../../common/AutoModalProvider';
import { EmployerPurchaseProductType } from '../../../common/interfaces/entities';
import { CreditProductPurchaseSuccessState } from '../../../common/interfaces/routerState';
import ContactPacksSuccessModalContent from '../PurchaseModals/CreditProductPurchaseSuccessModal/ContactPacksSuccessModalContent';
import { GlintsVIPSuccessModalContent } from '../PurchaseModals/CreditProductPurchaseSuccessModal/GlintsVIPSuccessModalContent';
import { JobBoostSuccessModalContent } from '../PurchaseModals/CreditProductPurchaseSuccessModal/JobBoostSuccessModalContent';
import PremiumPlanSuccessModalContent from '../PurchaseModals/CreditProductPurchaseSuccessModal/PremiumPlanSuccessModalContent';
import { RecommendedTalentChatSuccessModalContent } from '../PurchaseModals/CreditProductPurchaseSuccessModal/RecommendedTalentChatSuccessModalContent';
import { purchaseSuccessModalStyle } from '../PurchaseModals/CreditProductPurchaseSuccessModal/styles.sc';
import { TalentSearchSuccessModalContent } from '../PurchaseModals/CreditProductPurchaseSuccessModal/TalentSearchSuccessModalContent';
import { trackPaidFeatureSuccessfullyAdded } from '../PurchaseModals/PurchaseProductOrderSummarySection/tracking';
import { CreditProductPurchaseOrderForAfterPayment } from '../PurchaseModals/types';

type ComponentTypeProps = {
  onClose: () => void;
  creditProductPurchaseOrder: CreditProductPurchaseOrderForAfterPayment;
};

type ModalContent = ComponentType<ComponentTypeProps> & {
  ModalTitle: ComponentType<ComponentTypeProps>;
  ModalFooter: ComponentType<ComponentTypeProps>;
};

const MappedModalContentBasedOnType: Record<
  EmployerPurchaseProductType,
  ModalContent
> = {
  [EmployerPurchaseProductType.HOT_JOB]: JobBoostSuccessModalContent,
  [EmployerPurchaseProductType.RECOMMENDED_TALENT_CHAT]:
    RecommendedTalentChatSuccessModalContent,
  [EmployerPurchaseProductType.VIP_MEMBERSHIP]: GlintsVIPSuccessModalContent,
  [EmployerPurchaseProductType.TALENT_SEARCH]: TalentSearchSuccessModalContent,
  [EmployerPurchaseProductType.PREMIUM_PLAN]: PremiumPlanSuccessModalContent,
  [EmployerPurchaseProductType.SHORTLIST]: ContactPacksSuccessModalContent,
};

type Params = {
  onClose?: (
    creditProductPurchaseSuccess?: CreditProductPurchaseSuccessState['creditProductPurchaseSuccess']
  ) => void;
  productTypesToHandle?: EmployerPurchaseProductType[];
};

// VIP_MEMBERSHIP is handled globally, so we don't need to handle it here
const defaultProductTypesToHandle: Exclude<
  EmployerPurchaseProductType,
  typeof EmployerPurchaseProductType.VIP_MEMBERSHIP
>[] = Object.values(
  omit(EmployerPurchaseProductType, [
    EmployerPurchaseProductType.VIP_MEMBERSHIP,
    EmployerPurchaseProductType.PREMIUM_PLAN,
  ])
);

export default function useShowCreditProductPurchaseSuccessModalAsAlert(
  params?: Params
) {
  const { onClose } = params || {};

  const location = useLocation<CreditProductPurchaseSuccessState | undefined>();
  const history = useHistory();
  const { open: showModal, close: closeModal } = useModal();
  const purchaseSuccess = location.state?.creditProductPurchaseSuccess;
  const canShow = useAutoModal(
    AutoModalPriority.Transaction,
    Boolean(
      purchaseSuccess &&
        (params?.productTypesToHandle || defaultProductTypesToHandle).includes(
          purchaseSuccess.product.type
        )
    )
  );
  const showAlertCreditProductPurchaseSuccessWithLocationState = () => {
    if (!location.state?.creditProductPurchaseSuccess) {
      return;
    }

    const typesToHandle =
      params?.productTypesToHandle || defaultProductTypesToHandle;

    const { creditProductPurchaseSuccess } = location.state;

    const productType = creditProductPurchaseSuccess.product.type;

    if (!typesToHandle.includes(productType)) {
      return;
    }

    if (location.state?.creditProductPurchaseSuccess) {
      const isUsingDefaultHeader =
        productType !== EmployerPurchaseProductType.VIP_MEMBERSHIP;
      trackPaidFeatureSuccessfullyAdded(
        productType,
        creditProductPurchaseSuccess.companyProductInventory?.amount ?? 0
      );

      const handleOnClose = () => {
        closeModal();
        const nextState = omit(location.state, 'creditProductPurchaseSuccess');

        history.replace({
          ...location,
          state: nextState,
        });

        onClose?.(creditProductPurchaseSuccess);
      };

      const Title = () => {
        const Component = MappedModalContentBasedOnType[productType].ModalTitle;

        return (
          <Component
            {...{
              creditProductPurchaseOrder: creditProductPurchaseSuccess,
              onClose: handleOnClose,
            }}
          />
        );
      };

      const Footer = () => {
        const Component =
          MappedModalContentBasedOnType[productType].ModalFooter;

        return (
          <Component
            {...{
              creditProductPurchaseOrder: creditProductPurchaseSuccess,
              onClose: handleOnClose,
            }}
          />
        );
      };

      const Content = () => {
        const Component = MappedModalContentBasedOnType[productType];

        return (
          <Component
            {...{
              creditProductPurchaseOrder: creditProductPurchaseSuccess,
              onClose: handleOnClose,
            }}
          />
        );
      };

      showModal({
        header: <Title />,
        customActions: <Footer />,
        onClose: handleOnClose,
        closeOnClickOutside: false,
        children: <Content />,
        headerHasDefaultPadding: isUsingDefaultHeader,
        showCloseButton: false,
        style: purchaseSuccessModalStyle,
        zIndexOverride: 1000,
      });
    }
  };

  // using useRef to bypass having infinite rerenders due to useEffect deps as here deps are nested objects which changes reference every time
  const showAlertCreditProductPurchaseSuccessWithLocationStateRef = useRef(
    showAlertCreditProductPurchaseSuccessWithLocationState
  );
  // every time this hook is called, we want to update the ref to the latest function
  showAlertCreditProductPurchaseSuccessWithLocationStateRef.current =
    showAlertCreditProductPurchaseSuccessWithLocationState;

  useEffect(() => {
    if (canShow) {
      showAlertCreditProductPurchaseSuccessWithLocationStateRef.current();
    }
  }, [canShow, location.state?.creditProductPurchaseSuccess]);

  return null;
}
